import React, { useCallback, useContext as useContextCore, useMemo } from 'react'
import { useLocalStorage } from 'react-use'
import type { Asset, Product } from '@ztechapps/common.contract'
import { useLocalListAction, useCreateAsset, useUpdateAsset, useDeleteAsset } from '@ztechapps/common.hook'
import type {
  UseLocalListActionResult,
  AssetEntityType,
  CreateAssetInput,
  UpdateAssetInput,
} from '@ztechapps/common.hook'
import type { OnSetFileMetaFunction } from '@ztechapps/webui.input'
import type { DragSortEvent } from '@ztechapps/webui.util'

export type ViewMode = 'grid' | 'list'

const ASSET_VIEW_MODE_STORAGE_KEY = 'asset-container-view-mode'

export type ContextProps = {
  children?: React.ReactNode
  assets: Asset[]
  /**
   * The entity type for the asset container.
   * This determines which GraphQL mutation is used (e.g., createProduct_assets, createOrder_assets).
   */
  entityType: AssetEntityType
  /**
   * The ID of the parent entity (Product, Order, Task, Project, or PurchaseOrder).
   */
  entityId: string
  /**
   * Callback for reordering assets (bulk update via drag-and-drop sorting).
   * This is the only use case where updating the entire assets array is needed.
   */
  onReorder?: (value: Partial<Product>) => Promise<void>
  canEdit?: boolean
  pdfIcon: string
  fileIcon: string
  cloudinaryAppSettings?: { folder: string; cloud_name: string }
  coverImage?: string
  onSetFileMeta?: OnSetFileMetaFunction
}

export type ContextType = {
  allAssets: Asset[]
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  localListAction: UseLocalListActionResult<Asset>
  /**
   * Create a new asset on the entity.
   */
  onCreateAsset: (input: CreateAssetInput) => Promise<void>
  /**
   * Update an existing asset by ID.
   */
  onUpdateAsset: (assetId: string, input: UpdateAssetInput) => Promise<void>
  /**
   * Delete an asset by ID.
   */
  onDeleteAsset: (assetId: string) => Promise<void>
} & Omit<ContextProps, 'children'>

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const Provider = ({
  children,
  assets = [],
  entityType,
  entityId,
  onReorder,
  canEdit = true,
  pdfIcon,
  fileIcon,
  cloudinaryAppSettings,
  coverImage,
  onSetFileMeta,
}: ContextProps) => {
  // Local list action for filtering, searching, and sorting
  const localListAction = useLocalListAction<Asset>(assets, {
    defaultSort: null, // Assets use drag-sort, not field-based sort
  })

  const allAssets = localListAction.list

  // Persist view mode to localStorage - shared across all pages/components
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>(ASSET_VIEW_MODE_STORAGE_KEY, 'grid')

  // Initialize mutation hooks for child resolver operations
  const [createAssetMutation] = useCreateAsset(entityType)
  const [updateAssetMutation] = useUpdateAsset(entityType)
  const [deleteAssetMutation] = useDeleteAsset(entityType)

  // Create asset handler using child resolver mutation
  const onCreateAsset = useCallback(
    async (input: CreateAssetInput) => {
      await createAssetMutation({
        filter: { _id: entityId },
        input,
      })
    },
    [createAssetMutation, entityId]
  )

  // Update asset handler using child resolver mutation
  const onUpdateAsset = useCallback(
    async (assetId: string, input: UpdateAssetInput) => {
      await updateAssetMutation({
        filter: { _id: entityId, 'assets._id': assetId },
        input,
      })
    },
    [updateAssetMutation, entityId]
  )

  // Delete asset handler using child resolver mutation
  const onDeleteAsset = useCallback(
    async (assetId: string) => {
      await deleteAssetMutation({
        filter: { _id: entityId, 'assets._id': assetId },
      })
    },
    [deleteAssetMutation, entityId]
  )

  const contextValue = useMemo(
    () => ({
      allAssets,
      viewMode,
      setViewMode,
      localListAction,
      onReorder,
      onCreateAsset,
      onUpdateAsset,
      onDeleteAsset,
      assets,
      canEdit,
      entityType,
      entityId,
      pdfIcon,
      fileIcon,
      cloudinaryAppSettings,
      coverImage,
      onSetFileMeta,
    }),
    [
      allAssets,
      viewMode,
      setViewMode,
      localListAction,
      onReorder,
      onCreateAsset,
      onUpdateAsset,
      onDeleteAsset,
      assets,
      canEdit,
      entityType,
      entityId,
      pdfIcon,
      fileIcon,
      cloudinaryAppSettings,
      coverImage,
      onSetFileMeta,
    ]
  )

  return <CoreProvider value={contextValue}>{children}</CoreProvider>
}

export const useAssetPageContext = () => useContextCore(context)

/**
 * Hook for handling asset drag-and-drop sorting.
 *
 * Note: Asset reordering still uses the legacy onUpdate callback because
 * reordering inherently requires updating the order of items in the array.
 * Individual child resolver mutations cannot efficiently handle reordering.
 *
 * If frequent reordering is expected, consider adding a sortOrder field
 * to the Asset model and updating it via individual mutations.
 */
export const useAssetSortHandler = () => {
  const { allAssets, onReorder } = useAssetPageContext()

  return useCallback(
    async (e: DragSortEvent) => {
      const oldIndex = e.oldIndex
      const newIndex = e.newIndex
      if (oldIndex === undefined || newIndex === undefined) return

      if (!onReorder) {
        return
      }

      const updatedItems = [...allAssets]
      const [movedItem] = updatedItems.splice(oldIndex, 1)
      updatedItems.splice(newIndex, 0, movedItem)
      await onReorder({ assets: updatedItems })
    },
    [allAssets, onReorder]
  )
}
